import { SortInfo } from '@shared/interfaces/sortInfo';
import { MobileSort } from '@shared/components/Table/components/MobileSort';
import Skeleton from 'react-loading-skeleton';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useStyles } from './Table.styles';
import { TableHeader } from './components/TableHeader/TableHeader';
import classNames from 'classnames';
import noop from 'lodash/noop';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import throttle from 'lodash/throttle';
import { RocketLoader } from '../RocketLoader';
import { TableHeaderData } from '@shared/interfaces/TableHeaderData';

const SCROLL_THROTTLE_MS = 500;

interface CustomStyles {
  root?: string;
  table?: string;
  tableHeaderRow?: string;
  tableDataRow?: string;
}
interface Props {
  sortInfo?: SortInfo;
  isDataLoading: boolean;
  onSortClick?: (sortInfo: SortInfo) => void;
  onScrollThresholdPass?: () => void;
  tableHeaders: TableHeaderData[];
  children: ReactNode;
  scrollTresholdPx?: number;
  skeleton?: ReactNode;
  customStyles?: CustomStyles;
  isDataChunkLoading?: boolean;
  style?: React.CSSProperties;
}

export const Table: FC<Props> = ({
  sortInfo,
  isDataLoading,
  isDataChunkLoading = false,
  onSortClick,
  onScrollThresholdPass = noop,
  scrollTresholdPx = 1500,
  tableHeaders,
  children,
  skeleton,
  customStyles,
  style,
}) => {
  const styles = useStyles();
  const [isScrollToTopButtonVisible, setIsScrollToTopButtonVisible] = useState(false);
  const tableHeadersBlock = tableHeaders.map((tableHeaderData, i) => (
    <td key={i}>
      <TableHeader sortInfo={sortInfo} onSortClick={onSortClick} {...tableHeaderData}></TableHeader>
    </td>
  ));

  useEffect(() => {
    const handleDocumentScroll = throttle(() => {
      if (
        document.documentElement.scrollHeight -
          document.documentElement.scrollTop -
          document.documentElement.clientHeight <
        scrollTresholdPx
      ) {
        onScrollThresholdPass();
      }
    }, SCROLL_THROTTLE_MS);
    document.addEventListener('scroll', handleDocumentScroll);

    return () => document.removeEventListener('scroll', handleDocumentScroll);
  }, [onScrollThresholdPass]);

  useEffect(() => {
    const handleDocumentScroll = throttle(() => {
      const isButtonVisible = document.documentElement.scrollTop > document.documentElement.clientHeight * 2;
      setIsScrollToTopButtonVisible(isButtonVisible);
    }, SCROLL_THROTTLE_MS);
    document.addEventListener('scroll', handleDocumentScroll);

    return () => document.removeEventListener('scroll', handleDocumentScroll);
  }, [onScrollThresholdPass]);

  const tableSkeleton =
    skeleton ||
    Array(5)
      .fill(0)
      .map((_, i) => (
        <tr className={classNames(styles.tableDataRow, customStyles?.tableDataRow)} key={i}>
          {tableHeaders.map((_, j) => (
            <td key={j}>
              <Skeleton className={styles.textSkeleton} />
            </td>
          ))}
        </tr>
      ));

  const scrollToTop = () =>
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

  return (
    <div className={classNames(styles.root, customStyles?.root)}>
      {isScrollToTopButtonVisible && (
        <div className={styles.backToTopButton} onClick={scrollToTop}>
          <ArrowUpwardIcon></ArrowUpwardIcon>
        </div>
      )}
      {sortInfo && onSortClick && (
        <div className={styles.sortHolder}>
          <MobileSort sortInfo={sortInfo} tableHeaders={tableHeaders} onSortClick={onSortClick}></MobileSort>
        </div>
      )}
      <table style={style} className={classNames(styles.table, customStyles?.table)} cellSpacing={0}>
        <thead>
          <tr className={classNames(styles.tableHeaderRow, customStyles?.tableHeaderRow)}>{tableHeadersBlock}</tr>
        </thead>
        <tbody>
          {isDataLoading ? tableSkeleton : children}
          {isDataChunkLoading && (
            <tr>
              <td colSpan={999}>
                <div className={styles.loaderHolder}>
                  <RocketLoader></RocketLoader>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
